import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Combobox } from 'medere-ui-framework';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { AgendaSchedule, AgendaViewMode, ComboOption, PatientAppointmentWarning, ScheduleTagDTO, ScheduleSetup, PriorityDTO, AppointmentPractice } from '../types/agenda.types';
import { Establishment } from '../../../interface/interface';
import { AppointmentsDTO } from '../../../interface/interface';
import {
  getAgendaSchedule,
  getAgendaOptions,
  getHealthEntitiesCombo,
  getHealthEntityAppointmentsCategory,
  getPatientAppointmentWarnings,
  bookAppointment,
  bookMultisession,
  getScheduleSetupTags,
  getSpontaneousSetups,
  getFirstAvailableDate,
  getPriorities,
} from '../services/AgendaService';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { parseApiError } from '../types/error.types';
import { getStatusInfo } from '../utils/statusUtils';
import './NewAppointmentModal.scss';
import { loadAllEstablishments } from '../../../servicesHooks/Location/useLocationConfigService';
import PatientSelector, { PatientSelectorValue } from './PatientSelector';
import PracticesEditor from './PracticesEditor';
import { hasSenderPermissionByTaskMessageTypeAndMessageType } from '../../../servicesHooks/DigitalRP/useDigitalRPService';
import { EMessageType, ETaskMessageType } from '../../Recipe/RecipeEnums';

interface NewAppointmentModalProps {
  onClose: () => void;
  onBooked: () => void;
  onError?: (message: string) => void;
  initialDate?: Date;
  initialAgendas?: AgendaSchedule[];
  /** Professional whose agendas start selected when no `initialAgendas` are given. */
  initialProfessionalId?: number | null;
  initialBuildingId?: number | null;
  initialSlotTime?: string | null;
  initialViewMode?: AgendaViewMode;
  initialPatientId?: number | null;
  coverageLimited?: boolean;
  /** Forces the modal into a specific mode. When set, the single/series toggle is hidden. */
  mode?: 'single' | 'series';
}

type SlotFilter = 'all' | 'available' | 'occupied';
type AppointmentKind = 'simple' | 'double' | 'triple' | 'newPatient';

const APPOINTMENT_KIND_OPTIONS: { label: string; value: AppointmentKind }[] = [
  { label: 'Simple', value: 'simple' },
  { label: 'Doble', value: 'double' },
  { label: 'Triple', value: 'triple' },
  { label: 'Nuevo paciente', value: 'newPatient' },
];


function formatWarningDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, "dd/MM/yyyy 'a las' HH:mm");
}

function getAgendaDisplayName(agenda: AgendaSchedule): string {
  return agenda.agendaType === 'service'
    ? agenda.name
    : [agenda.professionalName, agenda.speciality, agenda.name].filter(Boolean).join(' — ');
}

const ALL_SERVICES_OPTION: AgendaSchedule & { displayName: string } = Object.freeze({
  templateIds: [], name: 'Todos', professionalId: 0, professionalName: '',
  speciality: null, specialityIds: [], agendaType: 'service', displayName: 'Todos',
});
const ALL_PROFESSIONALS_OPTION: AgendaSchedule & { displayName: string } = Object.freeze({
  templateIds: [], name: 'Todos', professionalId: 0, professionalName: '',
  speciality: null, specialityIds: [], agendaType: 'professional', displayName: 'Todos',
});
const isAllOption = (a: AgendaSchedule) => a.templateIds.length === 0;

export default function NewAppointmentModal({
  onClose,
  onBooked,
  onError,
  initialDate,
  initialAgendas = [],
  initialProfessionalId = null,
  initialBuildingId = null,
  initialSlotTime,
  initialViewMode = 'professional',
  initialPatientId,
  coverageLimited = false,
  mode,
}: NewAppointmentModalProps) {
  // Patient (managed by PatientSelector)
  const [patientValue, setPatientValue] = useState<PatientSelectorValue>({ mode: 'identify', data: null });
  const [patientResetKey, setPatientResetKey] = useState(0);

  // Derived patient accessors
  const patientMode = patientValue.mode;
  const selectedPatient = patientValue.mode === 'identify' ? patientValue.data?.patient ?? null : null;

  // Appointment type (locked when `mode` prop is set)
  const [appointmentType, setAppointmentType] = useState<'single' | 'series'>(mode ?? 'single');
  const [appointmentKind, setAppointmentKind] = useState<AppointmentKind>('simple');

  // Unified agenda selector state (professional and service agendas cannot be mixed).
  const [agendaOptions, setAgendaOptions] = useState<AgendaSchedule[]>([]);
  const [selectedAgendas, setSelectedAgendas] = useState<AgendaSchedule[]>(initialAgendas);
  // One-shot: on the first agenda load, select every agenda of the initial professional available for that
  // date and establishment. Explicit initialAgendas take precedence.
  const pendingProfessionalPreselect = useRef(initialAgendas.length === 0 && !!initialProfessionalId);
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate ?? new Date());
  const [slots, setSlots] = useState<AppointmentsDTO[]>([]);
  const [slotFilter, setSlotFilter] = useState<SlotFilter>('available');
  const [selectedSlot, setSelectedSlot] = useState<AppointmentsDTO | null>(null);
  const [establishmentOptions, setEstablishmentOptions] = useState<Establishment[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(initialBuildingId);
  const [isOverturn, setIsOverturn] = useState(false);
  const [isOutOfAgenda, setIsOutOfAgenda] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showFutureDays] = useState(true);
  const [loadingMoreSlots, setLoadingMoreSlots] = useState(false);
  const [allSlotsLoaded, setAllSlotsLoaded] = useState(false);
  const [scheduleTags, setScheduleTags] = useState<ScheduleTagDTO[]>([]);
  const [isSpontaneous, setIsSpontaneous] = useState(false);
  const [spontaneousSetups, setSpontaneousSetups] = useState<ScheduleSetup[]>([]);
  const [selectedSetupId, setSelectedSetupId] = useState<number | null>(null);

  // Detail fields
  const [reason, setReason] = useState('');
  const [priorityOptions, setPriorityOptions] = useState<PriorityDTO[]>([]);
  const [priority, setPriority] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [notifyWhatsApp, setNotifyWhatsApp] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [disabledWhatsApp, setDisabledWhatsApp] = useState(false);
  const [disabledEmail, setDisabledEmail] = useState(false);

  // Practices (Ordenes de estudios)
  const [practices, setPractices] = useState<AppointmentPractice[]>([]);

  useEffect(() => {
    setSelectedBuildingId(initialBuildingId);
  }, [initialBuildingId]);

  // Load priority options on mount
  useEffect(() => {
    getPriorities()
      .then(setPriorityOptions)
      .catch(() => setPriorityOptions([]));
  }, []);

  // Check sender permissions for notifications
  useEffect(() => {
    Promise.all([
      hasSenderPermissionByTaskMessageTypeAndMessageType(EMessageType.WHATSAPP, ETaskMessageType.APPOINTMENTS),
      hasSenderPermissionByTaskMessageTypeAndMessageType(EMessageType.EMAIL, ETaskMessageType.APPOINTMENTS),
    ]).then(([waDto, emailDto]) => {
      const waEnabled = !!waDto?.hassenderpermission;
      const emailEnabled = !!emailDto?.hassenderpermission;
      setDisabledWhatsApp(!waEnabled);
      setDisabledEmail(!emailEnabled);
      if (!waEnabled) setNotifyWhatsApp(false);
      if (!emailEnabled) setNotifyEmail(false);
    }).catch(() => {
      setDisabledWhatsApp(true);
      setDisabledEmail(true);
      setNotifyWhatsApp(false);
      setNotifyEmail(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedSlot) {
      setScheduleTags([]);
      return;
    }
    getScheduleSetupTags(selectedSlot.medicalschedulesetup)
      .then(setScheduleTags)
      .catch(() => setScheduleTags([]));
  }, [selectedSlot]);

  // Coverage limitation
  const [coverageEntity, setCoverageEntity] = useState<ComboOption | null>(null);
  const [appointmentsCategory, setAppointmentsCategory] = useState<string | null>(null);
  const [pendingOverQuotaSlot, setPendingOverQuotaSlot] = useState<{ slot: AppointmentsDTO; overturn: boolean } | null>(null);

  // Multisession state
  const [sessionCount, setSessionCount] = useState<number>(10);
  const [repeatEvery, setRepeatEvery] = useState<number>(7);
  const [repeatUnit, setRepeatUnit] = useState<'días' | 'semanas'>('días');
  const [suggestedTime, setSuggestedTime] = useState<string>('09:00');
  const [seriesStartDate, setSeriesStartDate] = useState<Date>(initialDate ?? new Date());
  const [sessionEntries, setSessionEntries] = useState<{
    date: Date;
    time: string;
    conflict: boolean;
    duplicate: boolean;
    availableSlots: { time: string; available: boolean; medicalschedulesetup: number; durationtooccupy: number; professionalName: string }[];
    loadingSlots: boolean;
    noSchedule: boolean;
  }[]>([]);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [checkingWarnings, setCheckingWarnings] = useState(false);
  const [appointmentWarnings, setAppointmentWarnings] = useState<PatientAppointmentWarning[]>([]);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);
  const slotsRequestId = useRef(0);
  const slotListRef = useRef<HTMLDivElement | null>(null);
  const autoScrolledSlotKey = useRef<string | null>(null);

  const selectedAgendaType: AgendaViewMode | null = selectedAgendas[0]?.agendaType ?? null;
  const bookingMode: AgendaViewMode = selectedAgendaType ?? initialViewMode;
  const selectedTemplateIds = useMemo(
    () => Array.from(new Set(selectedAgendas.flatMap((agenda) => agenda.templateIds))),
    [selectedAgendas],
  );
  const professionalIds = useMemo(
    () => Array.from(new Set(
      selectedAgendas
        .filter((agenda) => agenda.agendaType === 'professional' && agenda.professionalId > 0)
        .map((agenda) => agenda.professionalId),
    )),
    [selectedAgendas],
  );
  const specialityIds = useMemo(
    () => Array.from(new Set(
      selectedAgendas
        .filter((agenda) => agenda.agendaType === 'service')
        .flatMap((agenda) => agenda.specialityIds ?? []),
    )),
    [selectedAgendas],
  );
  const selectedTemplateIdsKey = selectedTemplateIds.join(',');
  const professionalIdsKey = professionalIds.join(',');
  const specialityIdsKey = specialityIds.join(',');
  const selectedDateKey = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);
  const qtyAppointments = appointmentKind === 'double' ? 2 : appointmentKind === 'triple' ? 3 : 1;
  const isNewPatient = appointmentKind === 'newPatient';
  const initialAgendasKey = initialAgendas.map((agenda) => `${agenda.agendaType}:${agenda.templateIds.join(',')}`).join('|');
  const initialDateKey = initialDate ? format(initialDate, 'yyyy-MM-dd') : null;
  const initialSlotTimeKey = initialSlotTime ? initialSlotTime.substring(0, 5) : null;
  const selectedProfessionalId = bookingMode === 'professional'
    ? (selectedSlot?.professional > 0 ? selectedSlot.professional : professionalIds[0] ?? null)
    : null;
  const selectedSpecialityId = bookingMode === 'service'
    ? (selectedSlot?.medicalspeciality > 0 ? selectedSlot.medicalspeciality : specialityIds[0] ?? null)
    : null;

  const getSlotProfessionalName = useCallback((slot: AppointmentsDTO): string => {
    const professionalName = slot.professionalnames?.trim();
    if (professionalName) return professionalName;
    return slot.specialityname?.trim() ?? '';
  }, []);

  const canLoadSlots = selectedBuildingId !== null && selectedAgendas.length > 0 && (
    bookingMode === 'professional' ? professionalIds.length > 0 : specialityIds.length > 0
  ) && (!coverageLimited || !!coverageEntity);

  useEffect(() => {
    if (!isSpontaneous || selectedTemplateIds.length === 0) {
      setSpontaneousSetups([]);
      setSelectedSetupId(null);
      return;
    }
    getSpontaneousSetups(selectedTemplateIds, selectedDateKey, selectedBuildingId)
      .then((setups) => {
        setSpontaneousSetups(setups);
        setSelectedSetupId(setups.length === 1 ? setups[0].setupId : null);
      })
      .catch(() => {
        setSpontaneousSetups([]);
        setSelectedSetupId(null);
      });
  }, [isSpontaneous, selectedTemplateIdsKey, selectedDateKey, selectedBuildingId]);

  const agendaOptionsWithLabels = useMemo(
    () => agendaOptions.map((agenda) => ({
      ...agenda,
      displayName: getAgendaDisplayName(agenda),
    })),
    [agendaOptions],
  );
  const serviceOptions = useMemo(
    () => agendaOptionsWithLabels.filter((a) => a.agendaType === 'service'),
    [agendaOptionsWithLabels],
  );
  const professionalOptions = useMemo(
    () => agendaOptionsWithLabels.filter((a) => a.agendaType === 'professional'),
    [agendaOptionsWithLabels],
  );
  const allServicesSelected = serviceOptions.length > 0 && serviceOptions.every((a) =>
    selectedAgendas.some((s) => s.templateIds.join(',') === a.templateIds.join(',')),
  );
  const allProfessionalsSelected = professionalOptions.length > 0 && professionalOptions.every((a) =>
    selectedAgendas.some((s) => s.templateIds.join(',') === a.templateIds.join(',')),
  );

  const groupedAgendaOptions = useMemo(() => [
    {
      label: 'Servicios',
      items: [ALL_SERVICES_OPTION, ...serviceOptions],
    },
    {
      label: 'Profesionales',
      items: [ALL_PROFESSIONALS_OPTION, ...professionalOptions],
    },
  ].filter((group) => group.items.length > 1), [serviceOptions, professionalOptions]);

  const effectiveSelectedAgendas = useMemo(() => {
    const value: AgendaSchedule[] = [...selectedAgendas];
    if (allServicesSelected) value.unshift(ALL_SERVICES_OPTION);
    if (allProfessionalsSelected) value.unshift(ALL_PROFESSIONALS_OPTION);
    return value;
  }, [selectedAgendas, allServicesSelected, allProfessionalsSelected]);

  const handleAgendaSelectionChange = useCallback((value: AgendaSchedule[]) => {
    const newSentinels = value.filter(isAllOption);
    const newReal = value.filter((a) => !isAllOption(a));

    const newHasAllServices = newSentinels.some((a) => a.agendaType === 'service');
    const newHasAllProf = newSentinels.some((a) => a.agendaType === 'professional');

    if (newHasAllServices && !allServicesSelected) {
      setSelectedAgendas(serviceOptions);
      return;
    }
    if (!newHasAllServices && allServicesSelected &&
        newReal.filter((a) => a.agendaType === 'service').length === serviceOptions.length) {
      setSelectedAgendas([]);
      return;
    }
    if (newHasAllProf && !allProfessionalsSelected) {
      setSelectedAgendas(professionalOptions);
      return;
    }
    if (!newHasAllProf && allProfessionalsSelected &&
        newReal.filter((a) => a.agendaType === 'professional').length === professionalOptions.length) {
      setSelectedAgendas([]);
      return;
    }

    const currentKeys = new Set(selectedAgendas.map((a) => `${a.agendaType}:${a.templateIds.join(',')}`));
    const newlySelected = newReal.find((a) => !currentKeys.has(`${a.agendaType}:${a.templateIds.join(',')}`));
    if (newlySelected && selectedAgendaType && newlySelected.agendaType !== selectedAgendaType) {
      setSelectedAgendas([newlySelected]);
      return;
    }
    setSelectedAgendas(newReal.filter((a) => !selectedAgendaType || a.agendaType === selectedAgendaType));
  }, [selectedAgendas, selectedAgendaType, allServicesSelected, allProfessionalsSelected, serviceOptions, professionalOptions]);

  // Load establishments on mount
  useEffect(() => {
    let active = true;
    loadAllEstablishments()
      .then((establishments) => {
        if (!active) return;
        const activeEstablishments = (establishments ?? []).filter((establishment) => establishment.active);
        setEstablishmentOptions(activeEstablishments);
        setSelectedBuildingId((current) => {
          if (initialBuildingId && activeEstablishments.some((establishment) => establishment.buildingid === initialBuildingId)) {
            return initialBuildingId;
          }
          if (current && activeEstablishments.some((establishment) => establishment.buildingid === current)) return current;
          return activeEstablishments.length === 1 ? activeEstablishments[0].buildingid : null;
        });
      })
      .catch(() => {
        if (active) setEstablishmentOptions([]);
      });
    return () => { active = false; };
  }, [initialBuildingId]);

  // Load agenda options
  useEffect(() => {
    if (!selectedBuildingId) {
      setAgendaOptions([]);
      setSelectedAgendas([]);
      return () => undefined;
    }
    let active = true;
    getAgendaOptions(undefined, selectedDateKey, selectedBuildingId, isSpontaneous || undefined)
      .then((options) => {
        if (!active) return;
        setAgendaOptions(options);
        if (pendingProfessionalPreselect.current) {
          pendingProfessionalPreselect.current = false;
          setSelectedAgendas(options.filter((option) =>
            option.agendaType === 'professional' && option.professionalId === initialProfessionalId));
          return;
        }
        setSelectedAgendas((current) => current
          .map((selected) => options.find((option) =>
            option.agendaType === selected.agendaType &&
            option.templateIds.join(',') === selected.templateIds.join(','),
          ))
          .filter((agenda): agenda is AgendaSchedule => !!agenda));
      })
      .catch(() => {
        if (active) setAgendaOptions([]);
    });
    return () => { active = false; };
  }, [selectedDateKey, selectedBuildingId, initialAgendasKey, isSpontaneous]);

  // Auto-set selectedDate to the first day with availability when agendas change
  useEffect(() => {
    if (isSpontaneous || selectedTemplateIds.length === 0 || !selectedBuildingId) return;
    let active = true;
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    getFirstAvailableDate(
      selectedTemplateIds,
      todayKey,
      selectedBuildingId,
      appointmentsCategory,
      qtyAppointments,
      isNewPatient,
    )
      .then((res) => {
        if (!active || !res.date) return;
        setSelectedDate(new Date(res.date + 'T00:00:00'));
      })
      .catch(() => { /* silently keep current date */ });
    return () => { active = false; };
  }, [selectedTemplateIdsKey, selectedBuildingId, appointmentsCategory, qtyAppointments, isNewPatient, isSpontaneous]);

  /** Recalculates the `conflict` flag for every entry, including duplicate date+time detection. */
  const recalcConflicts = useCallback((entries: typeof sessionEntries): typeof sessionEntries => {
    const seen = new Map<string, number[]>();
    entries.forEach((e, i) => {
      if (!e.time || e.loadingSlots) return;
      const key = `${format(e.date, 'yyyy-MM-dd')}|${e.time}`;
      const list = seen.get(key) ?? [];
      list.push(i);
      seen.set(key, list);
    });
    return entries.map((e, i) => {
      const key = `${format(e.date, 'yyyy-MM-dd')}|${e.time}`;
      const duplicates = seen.get(key);
      const isDuplicate = !!duplicates && duplicates.length > 1;
      const slotConflict = !e.time || e.noSchedule || (!e.availableSlots.find((s) => s.time === e.time && s.available) && !e.noSchedule);
      return { ...e, conflict: isDuplicate || slotConflict, duplicate: isDuplicate };
    });
  }, []);

  const loadSlotsForDate = useCallback(async (date: Date) => {
    if (!canLoadSlots) return { availableSlots: [], noSchedule: true };
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await getAgendaSchedule(
        dateStr,
        professionalIds.length > 0 ? professionalIds : null,
        specialityIds.length > 0 ? specialityIds : null,
        0,
        0,
        selectedTemplateIds.length > 0 ? selectedTemplateIds : null,
        false,
        appointmentsCategory,
        selectedBuildingId,
        qtyAppointments,
        isNewPatient,
      );
      const slots = res.data
        .filter((s) => s.initialhour)
        .map((s) => ({
          time: s.initialhour!,
          available: !!s.isavailable,
          medicalschedulesetup: s.medicalschedulesetup as number,
          durationtooccupy: (s.durationtooccupy as number) ?? 20,
          professionalName: getSlotProfessionalName(s),
        }));
      return { availableSlots: slots, noSchedule: slots.length === 0 };
    } catch {
      return { availableSlots: [], noSchedule: true };
    }
  }, [canLoadSlots, professionalIdsKey, specialityIdsKey, selectedTemplateIdsKey, appointmentsCategory, selectedBuildingId, qtyAppointments, isNewPatient, getSlotProfessionalName]);

  const handleGenerateDates = useCallback(async () => {
    if (!sessionCount || sessionCount < 1 || selectedAgendas.length === 0) return;
    const intervalDays = repeatUnit === 'semanas' ? repeatEvery * 7 : repeatEvery;
    const dates: Date[] = [];
    for (let i = 0; i < sessionCount; i++) {
      dates.push(addDays(seriesStartDate, i * intervalDays));
    }
    setSessionEntries(dates.map((date) => ({
      date, time: '', conflict: false, duplicate: false,
      availableSlots: [], loadingSlots: true, noSchedule: false,
    })));
    const results = await Promise.all(dates.map((date) => loadSlotsForDate(date)));
    const built = dates.map((date, i) => {
      const { availableSlots, noSchedule } = results[i];
      const suggestedAvailable = availableSlots.find((s) => s.time === suggestedTime && s.available);
      const firstAvailable = availableSlots.find((s) => s.available);
      const picked = suggestedAvailable?.time || firstAvailable?.time || '';
      return {
        date, time: picked, conflict: !picked || noSchedule, duplicate: false,
        availableSlots, loadingSlots: false, noSchedule,
      };
    });
    setSessionEntries(recalcConflicts(built));
  }, [sessionCount, repeatEvery, repeatUnit, suggestedTime, seriesStartDate, selectedAgendas.length, loadSlotsForDate, recalcConflicts]);

  const handleSessionDateChange = useCallback(async (idx: number, newDate: Date) => {
    const updated = [...sessionEntries];
    updated[idx] = { ...updated[idx], date: newDate, loadingSlots: true, availableSlots: [], time: '' };
    setSessionEntries(updated);
    const { availableSlots, noSchedule } = await loadSlotsForDate(newDate);
    const suggestedAvailable = availableSlots.find((s) => s.time === suggestedTime && s.available);
    const firstAvailable = availableSlots.find((s) => s.available);
    const picked = suggestedAvailable?.time || firstAvailable?.time || '';
    setSessionEntries((prev) => {
      const copy = [...prev];
      copy[idx] = {
        ...copy[idx], availableSlots, loadingSlots: false, noSchedule,
        time: picked, conflict: !picked || noSchedule,
      };
      return recalcConflicts(copy);
    });
  }, [sessionEntries, loadSlotsForDate, suggestedTime, recalcConflicts]);

  const handleSessionTimeChange = useCallback((idx: number, newTime: string) => {
    setSessionEntries((prev) => {
      const copy = [...prev];
      const slot = copy[idx].availableSlots.find((s) => s.time === newTime);
      copy[idx] = { ...copy[idx], time: newTime, conflict: !slot || !slot.available };
      return recalcConflicts(copy);
    });
  }, [recalcConflicts]);

  const conflictCount = useMemo(
    () => sessionEntries.filter((e) => e.conflict).length,
    [sessionEntries]
  );

  // Fetch appointments category when coverage entity changes (coverage-limited mode)
  useEffect(() => {
    if (!coverageLimited || !coverageEntity) {
      setAppointmentsCategory(null);
      return;
    }
    getHealthEntityAppointmentsCategory(coverageEntity.id)
      .then(({ category }) => setAppointmentsCategory(category))
      .catch(() => setAppointmentsCategory(null));
  }, [coverageLimited, coverageEntity?.id]);

  useEffect(() => {
    setAppointmentWarnings([]);
    setWarningsAcknowledged(patientMode === 'unidentified');
  }, [patientMode, selectedPatient?.id, selectedProfessionalId]);

  const PAGE_SIZE = 50;

  // Load slots
  useEffect(() => {
    const requestId = ++slotsRequestId.current;

    if (!canLoadSlots) {
      setLoadingSlots(false);
      setSlots([]);
      setAllSlotsLoaded(false);
      return;
    }

    setLoadingSlots(true);
    setSelectedSlot(null);
    setAllSlotsLoaded(false);
    getAgendaSchedule(
      selectedDateKey,
      professionalIds.length > 0 ? professionalIds : null,
      specialityIds.length > 0 ? specialityIds : null,
      0,
      PAGE_SIZE,
      selectedTemplateIds.length > 0 ? selectedTemplateIds : null,
      showFutureDays,
      appointmentsCategory,
      selectedBuildingId,
      qtyAppointments,
      isNewPatient,
    )
      .then((res) => {
        if (requestId === slotsRequestId.current) {
          setSlots(res.data);
          const initialSlot = initialSlotTimeKey
            ? res.data.find((slot) => slot.isavailable && slot.initialhour?.substring(0, 5) === initialSlotTimeKey)
            : null;
          setSelectedSlot(initialSlot ?? null);
          setAllSlotsLoaded(res.data.length < PAGE_SIZE);
        }
      })
      .catch((err) => {
        if (requestId === slotsRequestId.current) console.error(err);
      })
      .finally(() => {
        if (requestId === slotsRequestId.current) setLoadingSlots(false);
      });
  }, [canLoadSlots, professionalIdsKey, specialityIdsKey, selectedTemplateIdsKey, selectedDateKey, showFutureDays, appointmentsCategory, selectedBuildingId, qtyAppointments, isNewPatient, initialSlotTimeKey]);

  // Auto-scroll to initial slot
  useEffect(() => {
    if (!initialSlotTimeKey) {
      autoScrolledSlotKey.current = null;
      return;
    }
    if (loadingSlots || !selectedSlot) return;

    const targetKey = `${selectedDateKey}:${selectedTemplateIdsKey}:${initialSlotTimeKey}`;
    if (autoScrolledSlotKey.current === targetKey) return;

    const selectedElement = slotListRef.current?.querySelector<HTMLElement>('[data-slot-selected="true"]');
    if (!selectedElement) return;

    autoScrolledSlotKey.current = targetKey;
    const frameId = window.requestAnimationFrame(() => {
      selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [initialSlotTimeKey, selectedDateKey, selectedTemplateIdsKey, loadingSlots, selectedSlot]);

  const loadMoreSlots = useCallback(() => {
    if (!canLoadSlots || loadingMoreSlots || allSlotsLoaded) return;
    setLoadingMoreSlots(true);
    const offset = slots.length;
    getAgendaSchedule(
      selectedDateKey,
      professionalIds.length > 0 ? professionalIds : null,
      specialityIds.length > 0 ? specialityIds : null,
      offset,
      PAGE_SIZE,
      selectedTemplateIds.length > 0 ? selectedTemplateIds : null,
      showFutureDays,
      appointmentsCategory,
      selectedBuildingId,
      qtyAppointments,
      isNewPatient,
    )
      .then((res) => {
        setSlots(prev => [...prev, ...res.data]);
        setAllSlotsLoaded(res.data.length < PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoadingMoreSlots(false));
  }, [canLoadSlots, professionalIdsKey, specialityIdsKey, selectedTemplateIdsKey, selectedDateKey, showFutureDays, appointmentsCategory, selectedBuildingId, qtyAppointments, isNewPatient, slots.length, loadingMoreSlots, allSlotsLoaded]);

  const handleSlotSelect = useCallback((slot: AppointmentsDTO, overturn: boolean) => {
    if (coverageLimited && !slot.offerappointment) {
      setPendingOverQuotaSlot({ slot, overturn });
      return;
    }
    setSelectedSlot(slot);
    setIsOverturn(overturn);
    setIsOutOfAgenda(false);
  }, [coverageLimited]);

  const confirmOverQuotaSlot = useCallback(() => {
    if (!pendingOverQuotaSlot) return;
    setSelectedSlot(pendingOverQuotaSlot.slot);
    setIsOverturn(pendingOverQuotaSlot.overturn);
    setIsOutOfAgenda(false);
    setPendingOverQuotaSlot(null);
  }, [pendingOverQuotaSlot]);

  const filteredSlots = useMemo(() => {
    return slots.filter((s) =>
      s.isavailable ||
      (s.maxpatientsoutofschedulebyhour > 0 && s.qtyoutofscheduleapp < s.maxpatientsoutofschedulebyhour)
    );
  }, [slots]);

  /** Groups filteredSlots by date for rendering day separators when showFutureDays is on. */
  const slotsByDate = useMemo(() => {
    if (!showFutureDays) return null;
    const groups: { dateKey: string; label: string; slots: AppointmentsDTO[] }[] = [];
    let current: typeof groups[number] | null = null;
    for (const slot of filteredSlots) {
      const d = new Date(slot.appointmentdatetime);
      const dateKey = format(d, 'yyyy-MM-dd');
      if (!current || current.dateKey !== dateKey) {
        current = { dateKey, label: format(d, "EEE d MMM", { locale: es }), slots: [] };
        groups.push(current);
      }
      current.slots.push(slot);
    }
    return groups;
  }, [filteredSlots, showFutureDays]);

  // Check warnings when patient + professional are selected
  useEffect(() => {
    if (
      patientMode !== 'identify' ||
      !selectedPatient ||
      !selectedProfessionalId ||
      warningsAcknowledged
    ) return;

    let active = true;
    setCheckingWarnings(true);

    getPatientAppointmentWarnings(selectedPatient.id, selectedProfessionalId)
      .then((warnings) => {
        if (!active) return;
        setAppointmentWarnings(warnings);
        setWarningsAcknowledged(true);
      })
      .catch((err) => {
        if (!active) return;
        // Error general que no se muestra en la pantalla: card flotante (onError). "Confirmar turno" queda
        // deshabilitado con el motivo en su tooltip.
        onError?.(parseApiError(err, 'No se pudieron verificar los turnos anteriores del paciente.'));
      })
      .finally(() => {
        if (active) setCheckingWarnings(false);
      });

    return () => { active = false; };
  }, [patientMode, selectedPatient?.id, selectedProfessionalId, warningsAcknowledged]);

  const professionalSummary = selectedAgendas.find((agenda) => agenda.agendaType === 'professional')?.professionalName ?? '';

  // Book appointment
  const handleConfirm = useCallback(async () => {
    if (!canLoadSlots) return;
    let patientPayload: Record<string, unknown>;
    if (patientValue.mode === 'identify') {
      if (!patientValue.data) return;
      patientPayload = { patientId: patientValue.data.patientId };
    } else {
      const u = patientValue.data;
      if (!u.patientReference.trim() || !u.patientPhone.trim()) return;
      patientPayload = {
        patientReference: u.patientReference,
        patientPhone: u.patientPhone,
        patientDocument: u.patientDocument,
        ...(u.healthEntityPlanId ? { healthEntityPlanId: u.healthEntityPlanId } : {}),
        ...(u.benefitNumber ? { benefitNumber: u.benefitNumber } : {}),
      };
    }

    setSubmitting(true);
    try {
      if (appointmentType === 'single') {
        if (!isSpontaneous && !isOutOfAgenda && !selectedSlot) return;
        const useCurrentTime = isSpontaneous || isOutOfAgenda;
        const bookingDate = useCurrentTime ? new Date() : new Date(selectedSlot!.appointmentdatetime);
        const dateStr = format(bookingDate, 'yyyy-MM-dd');
        const timeStr = useCurrentTime
          ? format(bookingDate, 'HH:mm:ss')
          : (selectedSlot?.initialhour || '00:00');
        const appointmentDateTime = useCurrentTime
          ? `${dateStr}T${timeStr}`
          : `${dateStr}T${timeStr}:00`;
        await bookAppointment({
          ...patientPayload,
          buildingId: selectedBuildingId,
          scheduleSetupId: isSpontaneous
            ? selectedSetupId
            : isOutOfAgenda
              ? ((selectedSlot?.medicalschedulesetup as number | null) ?? null)
              : selectedSlot!.medicalschedulesetup as number,
          appointmentDateTime,
          duration: selectedSlot?.durationtooccupy || 20,
          professionalId: selectedProfessionalId,
          specialityId: selectedSpecialityId ?? null,
          notes,
          reason,
          priorityId: priority,
          practices: practices.map((p) => ({
            nomenclatorId: p.nomenclatorId,
            requestingDoctorId: p.requestingDoctorId,
            practiceDate: p.practiceDate,
            observations: p.observations,
          })),
          notifyWhatsApp,
          notifyEmail,
          outOfSchedule: isOverturn,
          outOfAgenda: isOutOfAgenda,
        });
      } else {
        if (sessionEntries.length === 0) return;
        const sessions = sessionEntries.map((entry) => {
          const matchingSlot = entry.availableSlots.find((s) => s.time === entry.time);
          return {
            date: format(entry.date, 'yyyy-MM-dd'),
            time: entry.time,
            scheduleSetupId: matchingSlot?.medicalschedulesetup ?? 0,
            duration: matchingSlot?.durationtooccupy ?? 20,
          };
        });
        await bookMultisession({
          ...patientPayload,
          buildingId: selectedBuildingId,
          professionalId: selectedProfessionalId,
          specialityId: selectedSpecialityId ?? null,
          notes: reason,
          sessions,
          notifyWhatsApp,
          notifyEmail,
        });
      }
      onBooked();
      onClose();
    } catch (err) {
      // Error general: solo la card flotante (onError), sin repetirlo en la pantalla
      onError?.(parseApiError(err, 'No se pudo agendar el turno. Intentá de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  }, [
    appointmentType, patientValue, selectedSlot,
    selectedProfessionalId, selectedSpecialityId, selectedDate, reason, notes, priority, practices,
    notifyWhatsApp, notifyEmail,
    sessionEntries, isOverturn, isOutOfAgenda, isSpontaneous, selectedSetupId, selectedBuildingId, canLoadSlots, onBooked, onClose,
  ]);

  const hasSessionConflicts = appointmentType === 'series' &&
    sessionEntries.some((e) => e.conflict || e.noSchedule || e.loadingSlots || !e.time);

  // Combined validation (all sections)
  const canGoToStep2 =
    canLoadSlots &&
    (appointmentType === 'single'
      ? (isSpontaneous ? !!selectedSetupId : (isOutOfAgenda || !!selectedSlot))
      : sessionEntries.length > 0 && !hasSessionConflicts);

  const canGoToStep3 = patientValue.mode === 'identify'
    ? !!patientValue.data
    : !!(patientValue.data.patientReference.trim() && patientValue.data.patientPhone.trim());

  const warningCheckReady = patientMode === 'unidentified' || warningsAcknowledged || bookingMode === 'service';
  const canConfirm = canGoToStep2 && canGoToStep3 && warningCheckReady && !submitting && !checkingWarnings;

  const dateLabel = useMemo(() => format(selectedDate, "EEE d MMM", { locale: es }), [selectedDate]);

  const busy = submitting || checkingWarnings;

  // Por qué "Confirmar turno" está deshabilitado (tooltip del botón): las mismas condiciones que canConfirm.
  const confirmBlockedReason = ((): string | null => {
    if (canConfirm || busy) return null;
    if (!selectedBuildingId) return 'Elegí un establecimiento';
    if (selectedAgendas.length === 0) return 'Elegí una agenda';
    if (coverageLimited && !coverageEntity) return 'Elegí la obra social del turno';
    if (appointmentType === 'single') {
      if (isSpontaneous && !selectedSetupId) return 'Elegí la configuración de agenda';
      if (!isSpontaneous && !isOutOfAgenda && !selectedSlot) return 'Elegí un horario';
    } else {
      if (sessionEntries.length === 0) return 'Generá las fechas de las sesiones';
      if (hasSessionConflicts) return 'Resolvé las sesiones con conflicto';
    }
    if (!canGoToStep3) {
      return patientValue.mode === 'identify'
        ? 'Elegí un paciente'
        : 'Completá el nombre de referencia y el teléfono del paciente';
    }
    if (!warningCheckReady) return 'No se pudieron verificar los turnos anteriores del paciente';
    return 'Completá los datos del turno';
  })();

  const showCoverageHint = coverageLimited && !coverageEntity;

  const renderSlot = (slot: AppointmentsDTO, key: string) => {
    const isSelected = selectedSlot === slot;
    const isAvailable = slot.isavailable;
    const canOverturn = !isAvailable && slot.maxpatientsoutofschedulebyhour > 0
      && slot.qtyoutofscheduleapp < slot.maxpatientsoutofschedulebyhour;
    const remainingOverturn = canOverturn ? slot.maxpatientsoutofschedulebyhour - slot.qtyoutofscheduleapp : 0;
    if (selectedSlot && !isSelected) return null;
    const professionalName = getSlotProfessionalName(slot);
    return (
      <div
        key={key}
        data-slot-selected={isSelected ? 'true' : undefined}
        className={classNames('slot-item', {
          'slot-item--selected': isSelected,
          'slot-item--occupied': !isAvailable && !canOverturn,
          'slot-item--overturn': canOverturn,
        })}
      >
        <span className="slot-item__time">{slot.initialhour}</span>
        {professionalName && (
          <span className="slot-item__agenda nv-tip" data-pr-tooltip={professionalName}>{professionalName}</span>
        )}
        <span className="slot-item__status">
          {isAvailable
            ? (isSelected && !isOverturn ? 'Libre · seleccionado' : 'Libre')
            : canOverturn
              ? (isSelected && isOverturn
                ? 'Sobreturno · seleccionado'
                : `${slot.patientname || 'Ocupado'} · ${remainingOverturn} sobreturno${remainingOverturn > 1 ? 's' : ''} disponible${remainingOverturn > 1 ? 's' : ''}`)
              : (slot.patientname || 'Ocupado')
          }
        </span>
        {coverageLimited && !slot.offerappointment && <Tag className="nv-tag-warn" value="Fuera de cupo" />}
        {isAvailable && !isSelected && (
          <Button type="button" className="buttonMain3" label="Elegir" onClick={() => handleSlotSelect(slot, false)} />
        )}
        {canOverturn && !(isSelected && isOverturn) && (
          <Button type="button" className="buttonMain3" label="Sobreturno" onClick={() => handleSlotSelect(slot, true)} />
        )}
        {isSelected && (
          <Button type="button" className="buttonMain3" label="Modificar" onClick={() => { setSelectedSlot(null); setIsOverturn(false); }} />
        )}
      </div>
    );
  };

  /** Fila única de "se registra ahora" (espontáneo o fuera de los horarios de la agenda). */
  const renderNowSlot = (text: string) => (
    <div className="slot-list">
      <div className="slot-item slot-item--selected">
        <span className="slot-item__time">{format(new Date(), 'HH:mm')}</span>
        <span className="slot-item__status">{text}</span>
        <i className="pi pi-check" aria-hidden="true" />
      </div>
    </div>
  );

  return (
    <>
    <div className="nv-scope nv-page new-appointment-screen">
      <header className="nv-page-head">
        <h1 className="nv-h1">{mode === 'series' ? 'Nuevas sesiones' : 'Nuevo turno'}</h1>
      </header>

      <div className="new-appointment-screen__body">
        {/* ══════════ TURNO ══════════ */}
        <section className="nv-section" aria-labelledby="nt-turn-title">
          <h2 id="nt-turn-title" className="nv-section-title">Turno</h2>

          {/* Espontáneo, Establecimiento, Tipo de turno, Agenda, Configuración */}
          <div className="nv-form-row">
            {appointmentType === 'single' && (
              <div className="nv-switch-field">
                <span className="nv-check-row">
                  <InputSwitch
                    inputId="nt-spontaneous"
                    checked={isSpontaneous}
                    onChange={(e) => {
                      const val = e.value ?? false;
                      setIsSpontaneous(val);
                      setSelectedSlot(null);
                      setSelectedSetupId(null);
                      setSpontaneousSetups([]);
                      if (val) {
                        setIsOutOfAgenda(false);
                        setIsOverturn(false);
                      }
                    }}
                  />
                  <label htmlFor="nt-spontaneous">Espontáneo</label>
                </span>
              </div>
            )}
            <div className="nv-field nv-w-m">
              <label htmlFor="nt-building">Establecimiento</label>
              <Dropdown
                inputId="nt-building"
                value={selectedBuildingId}
                options={establishmentOptions}
                optionLabel="buildingname"
                optionValue="buildingid"
                onChange={(e) => {
                  setSelectedBuildingId(e.value ?? null);
                  setSelectedAgendas([]);
                  setSelectedSlot(null);
                }}
                placeholder="Seleccionar"
                emptyMessage="No se encontraron establecimientos"
                emptyFilterMessage="No se encontraron establecimientos"
                panelClassName="nv-scope"
              />
            </div>
            {!isSpontaneous && (
              <div className="nv-field nv-w-s">
                <label htmlFor="nt-kind">Tipo de turno</label>
                <Dropdown
                  inputId="nt-kind"
                  value={appointmentKind}
                  options={APPOINTMENT_KIND_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => {
                    setAppointmentKind(e.value as AppointmentKind);
                    setSelectedSlot(null);
                  }}
                  panelClassName="nv-scope"
                />
              </div>
            )}
            <div className="nv-field nv-w-l">
              <label htmlFor="nt-agenda">Agenda</label>
              <MultiSelect
                inputId="nt-agenda"
                value={effectiveSelectedAgendas}
                options={groupedAgendaOptions}
                optionLabel="displayName"
                optionGroupLabel="label"
                optionGroupChildren="items"
                onChange={(e) => handleAgendaSelectionChange(e.value as AgendaSchedule[])}
                placeholder={selectedBuildingId ? 'Seleccionar' : 'Elegí un establecimiento primero'}
                disabled={!selectedBuildingId}
                filter
                filterPlaceholder="Buscar agenda"
                emptyFilterMessage="No se encontraron agendas"
                display="chip"
                selectedItemTemplate={(agenda) => {
                  if (!agenda) return '';
                  const a = agenda as AgendaSchedule;
                  if (isAllOption(a)) return a.agendaType === 'service' ? 'Todos los servicios' : 'Todos los profesionales';
                  if (a.agendaType === 'service' && allServicesSelected) return null;
                  if (a.agendaType === 'professional' && allProfessionalsSelected) return null;
                  return getAgendaDisplayName(a);
                }}
                panelClassName="nv-scope"
              />
            </div>
            {isSpontaneous && selectedAgendas.length > 0 && (
              <div className="nv-field nv-w-m">
                <label htmlFor="nt-setup">Configuración de agenda <span className="nv-req">*</span></label>
                <Dropdown
                  inputId="nt-setup"
                  value={selectedSetupId}
                  options={spontaneousSetups}
                  optionLabel="description"
                  optionValue="setupId"
                  onChange={(e) => setSelectedSetupId(e.value ?? null)}
                  placeholder="Seleccionar"
                  emptyMessage="No hay configuraciones disponibles"
                  panelClassName="nv-scope"
                  itemTemplate={(option: ScheduleSetup) => (
                    <span className="nv-stack-4">
                      <span className="nv-strong">{option.description}</span>
                      <span className="nv-help">{option.professional} · {option.activityType} · {option.location}</span>
                    </span>
                  )}
                />
              </div>
            )}
          </div>

          {/* Obra social, Fecha, Tipo (único / serie) */}
          {(coverageLimited || (appointmentType === 'single' && !isSpontaneous) || !mode) && (
            <div className="nv-form-row">
              {coverageLimited && (
                <div className="nv-field nv-w-m">
                  <label htmlFor="nt-coverage">Obra social del turno <span className="nv-req">*</span></label>
                  <Combobox
                    inputId="nt-coverage"
                    getItems={getHealthEntitiesCombo}
                    optionLabel="name"
                    value={coverageEntity}
                    setValue={(val: ComboOption | null) => { setCoverageEntity(val); setSelectedSlot(null); }}
                    placeholder="Seleccionar"
                  />
                </div>
              )}
              {appointmentType === 'single' && !isSpontaneous && (
                <div className="nv-field nv-w-s">
                  <label htmlFor="nt-date">Fecha</label>
                  <Calendar
                    inputId="nt-date"
                    value={selectedDate}
                    onChange={(e) => e.value && setSelectedDate(e.value as Date)}
                    dateFormat="dd/mm/yy"
                    showIcon
                    panelClassName="nv-scope"
                  />
                </div>
              )}
              {!mode && (
                <div className="nv-field">
                  <label id="nt-type-label">Tipo</label>
                  <SelectButton
                    aria-labelledby="nt-type-label"
                    value={appointmentType}
                    options={[
                      { label: 'Único', value: 'single' },
                      { label: 'Serie', value: 'series' },
                    ]}
                    onChange={(e) => {
                      if (!e.value) return;
                      setAppointmentType(e.value);
                      if (e.value === 'series') {
                        setIsSpontaneous(false);
                        setSelectedSetupId(null);
                        setSpontaneousSetups([]);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {showCoverageHint && (
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              <span>Elegí una obra social para ver los turnos disponibles.</span>
            </p>
          )}

          {!showCoverageHint && appointmentType === 'single' && isSpontaneous && !!selectedSetupId
            && renderNowSlot('Se registrará ahora como turno espontáneo')}

          {/* Serie: generador de fechas + sesiones */}
          {!showCoverageHint && appointmentType !== 'single' && (
            <>
              <div className="nv-form-row">
                <div className="nv-field nv-w-s">
                  <label htmlFor="nt-series-count">Sesiones</label>
                  <InputNumber inputId="nt-series-count" value={sessionCount} onValueChange={(e) => setSessionCount(e.value ?? 1)} min={1} max={100} showButtons />
                </div>
                <div className="nv-field nv-w-m">
                  <label htmlFor="nt-series-every">Repetir cada</label>
                  <div className="series-generator__repeat">
                    <InputNumber inputId="nt-series-every" value={repeatEvery} onValueChange={(e) => setRepeatEvery(e.value ?? 7)} min={1} max={365} className="series-repeat-number" />
                    <SelectButton
                      aria-label="Unidad de repetición"
                      value={repeatUnit}
                      options={[{ label: 'días', value: 'días' }, { label: 'semanas', value: 'semanas' }]}
                      onChange={(e) => e.value && setRepeatUnit(e.value)}
                      className="series-repeat-unit"
                    />
                  </div>
                </div>
                <div className="nv-field nv-w-s">
                  <label htmlFor="nt-series-from">Desde</label>
                  <Calendar inputId="nt-series-from" value={seriesStartDate} onChange={(e) => e.value && setSeriesStartDate(e.value as Date)} dateFormat="dd/mm/yy" showIcon panelClassName="nv-scope" />
                </div>
                <div className="nv-field nv-w-s">
                  <label htmlFor="nt-series-time">Hora sugerida</label>
                  <InputText id="nt-series-time" value={suggestedTime} onChange={(e) => setSuggestedTime(e.target.value)} placeholder="09:00" />
                </div>
              </div>
              <div className="nv-row-8">
                <Button type="button" className="buttonMain3" icon="pi pi-sync" label="Generar fechas" onClick={() => void handleGenerateDates()} />
              </div>
              {sessionEntries.length > 0 && (
                <div className="session-list">
                  <div className="session-list__header">
                    <span className="nv-strong">Sesiones — Ajustá el día y la hora de cada sesión</span>
                    {conflictCount > 0 && <Tag className="nv-tag-warn" value={`${conflictCount} con conflicto`} />}
                  </div>
                  <div className="session-list__body">
                    {sessionEntries.map((entry, idx) => (
                      <div key={idx} className="session-entry">
                        <span className="nv-strong">Sesión {idx + 1}</span>
                        <div className="nv-form-row">
                          <div className="nv-field nv-w-s">
                            <label htmlFor={`nt-session-${idx}-date`}>Fecha</label>
                            <Calendar
                              inputId={`nt-session-${idx}-date`}
                              value={entry.date}
                              onChange={(e) => { if (e.value) handleSessionDateChange(idx, e.value as Date); }}
                              dateFormat="dd/mm/yy"
                              showIcon
                              panelClassName="nv-scope"
                            />
                          </div>
                          <div className="nv-field nv-w-m">
                            <label htmlFor={`nt-session-${idx}-time`}>Hora</label>
                            {entry.loadingSlots ? (
                              <span className="nv-loading session-entry__loading" role="status">
                                <i className="pi pi-spinner pi-spin" aria-hidden="true" /> Cargando…
                              </span>
                            ) : (
                              <Dropdown
                                inputId={`nt-session-${idx}-time`}
                                value={entry.time}
                                options={entry.availableSlots.map((slot) => ({
                                  label: `${slot.time}${slot.professionalName ? ` — ${slot.professionalName}` : ''}${!slot.available ? ' (ocupado)' : ''}`,
                                  value: slot.time,
                                  disabled: !slot.available,
                                }))}
                                onChange={(e) => handleSessionTimeChange(idx, e.value)}
                                placeholder={entry.noSchedule ? 'Sin horarios' : 'Seleccionar'}
                                disabled={entry.noSchedule}
                                panelClassName="nv-scope"
                              />
                            )}
                          </div>
                        </div>
                        <div className="session-entry__status">
                          {entry.noSchedule ? (
                            <Tag className="nv-tag-neutral" value="Sin agenda" />
                          ) : entry.duplicate ? (
                            <Tag className="nv-tag-warn" value="Duplicado" />
                          ) : entry.conflict ? (
                            <Tag className="nv-tag-warn" value="Ocupado" />
                          ) : (
                            <Tag className="nv-tag-ok" value="Libre" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* ══════════ DISPONIBILIDAD ══════════ */}
        {!showCoverageHint && appointmentType === 'single' && !isSpontaneous && (
          <section className="nv-section" aria-labelledby="nt-availability-title">
            <div className="nv-section-head">
              <h2 id="nt-availability-title" className="nv-section-title">
                Disponibilidad <span className="nv-title-detail">— {dateLabel}</span>
              </h2>
              <span className="nv-check-row">
                <InputSwitch
                  inputId="nt-out-of-agenda"
                  checked={isOutOfAgenda}
                  onChange={(e) => {
                    setIsOutOfAgenda(e.value ?? false);
                    setIsOverturn(false);
                    setSelectedSlot(null);
                  }}
                />
                <label htmlFor="nt-out-of-agenda">Espontáneo</label>
              </span>
            </div>
            {isOutOfAgenda ? (
              renderNowSlot('Se registrará ahora, vinculado a la agenda seleccionada')
            ) : (
              <div ref={slotListRef} className={classNames('slot-list', { 'slot-list--future': showFutureDays })}>
                {loadingSlots && (
                  <div className="nv-loading slot-list__loading" role="status">
                    <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando horarios…
                  </div>
                )}
                {!loadingSlots && filteredSlots.length === 0 && selectedAgendas.length > 0 && (
                  <div className="slot-list__empty">No hay horarios disponibles.</div>
                )}
                {!loadingSlots && slotsByDate ? (
                  <>
                    {slotsByDate.map((group) => {
                      if (selectedSlot && !group.slots.includes(selectedSlot)) return null;
                      return (
                        <div key={group.dateKey}>
                          <div className="slot-list__day-header">{group.label}</div>
                          {group.slots.map((slot, idx) => renderSlot(slot, `${group.dateKey}-${slot.initialhour}-${idx}`))}
                        </div>
                      );
                    })}
                    {!allSlotsLoaded && !selectedSlot && (
                      <div className="slot-list__load-more">
                        <Button
                          type="button"
                          className="buttonMain3"
                          icon="pi pi-arrow-down"
                          label="Ver más días"
                          onClick={loadMoreSlots}
                          loading={loadingMoreSlots}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  !loadingSlots && filteredSlots.map((slot, idx) => renderSlot(slot, `${slot.initialhour}-${idx}`))
                )}
              </div>
            )}
            {selectedSlot && scheduleTags.length > 0 && (
              <div className="nv-row-8">
                <i className="pi pi-tags nv-muted" aria-hidden="true" />
                <span className="nv-muted">Notas de agenda:</span>
                {scheduleTags.map((tag) => (
                  <Tag key={tag.id} className="nv-tag-neutral" value={tag.name} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ══════════ PACIENTE ══════════ */}
        <section className="nv-section" aria-labelledby="nt-patient-title">
          <h2 id="nt-patient-title" className="nv-section-title">Paciente</h2>
          {/* Bloque propio: el selector (compartido) arma su layout en flujo normal, no como ítems del flex */}
          <div>
            <PatientSelector
              visible={true}
              initialPatientId={initialPatientId}
              allowCreate={isSpontaneous}
              onChange={setPatientValue}
              resetKey={patientResetKey}
            />
          </div>
        </section>

        {/* ══════════ DETALLE ══════════ */}
        <section className="nv-section" aria-labelledby="nt-detail-title">
          <h2 id="nt-detail-title" className="nv-section-title">Detalle</h2>
          <div className="nv-form-row">
            <div className="nv-field nv-w-m">
              <label htmlFor="nt-reason">Motivo de consulta</label>
              <InputText id="nt-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: control, dolor de cabeza" />
            </div>
            <div className="nv-field nv-w-s">
              <label htmlFor="nt-priority">Prioridad</label>
              <Dropdown
                inputId="nt-priority"
                value={priority}
                options={priorityOptions}
                optionLabel="description"
                optionValue="id"
                onChange={(e) => setPriority(e.value ?? null)}
                placeholder="Seleccionar"
                panelClassName="nv-scope"
              />
            </div>
            <div className="nv-field nv-w-l">
              <label htmlFor="nt-notes">Notas</label>
              <InputText id="nt-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas internas" />
            </div>
          </div>
          <div className="nv-field" role="group" aria-labelledby="nt-notify-label">
            <label id="nt-notify-label">Notificar al paciente</label>
            {/* TODO(design-system): el Checkbox deshabilitado se ve igual que uno habilitado sin marcar */}
            <div className="detail-notify__options">
              <span
                className="nv-check-row nv-tip"
                data-pr-tooltip={disabledWhatsApp ? 'No hay un remitente de WhatsApp habilitado para turnos' : undefined}
              >
                <Checkbox inputId="notify-whatsapp" checked={notifyWhatsApp} onChange={(e) => setNotifyWhatsApp(e.checked ?? false)} disabled={disabledWhatsApp} />
                <label htmlFor="notify-whatsapp" className={classNames('nv-check-row', { 'nv-muted': disabledWhatsApp })}><i className="pi pi-whatsapp" aria-hidden="true" /> WhatsApp</label>
              </span>
              <span
                className="nv-check-row nv-tip"
                data-pr-tooltip={disabledEmail ? 'No hay un remitente de email habilitado para turnos' : undefined}
              >
                <Checkbox inputId="notify-email" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.checked ?? false)} disabled={disabledEmail} />
                <label htmlFor="notify-email" className={classNames('nv-check-row', { 'nv-muted': disabledEmail })}><i className="pi pi-envelope" aria-hidden="true" /> Email</label>
              </span>
            </div>
          </div>
        </section>

        {/* ══════════ ÓRDENES DE ESTUDIOS ══════════ */}
        {appointmentType === 'single' && (
          <section className="nv-section" aria-labelledby="nt-practices-title">
            <h2 id="nt-practices-title" className="nv-section-title">Órdenes de estudios</h2>
            <div>
              <PracticesEditor practices={practices} onChange={setPractices} onError={onError} />
            </div>
          </section>
        )}

        {/* ── Turnos previos del paciente ── */}
        {checkingWarnings && (
          <div className="nv-loading" role="status">
            <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Verificando turnos previos del paciente…
          </div>
        )}

        {appointmentWarnings.length > 0 && (
          <div className="appointment-warnings" role="status">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              <span>
                El paciente ya tiene turnos con <span className="nv-strong">{professionalSummary}</span> que conviene revisar antes de continuar.
              </span>
            </p>
            <ul className="appointment-warnings__list">
              {appointmentWarnings.map((warning) => {
                const status = getStatusInfo(warning.stateId);
                return (
                  <li key={`${warning.appointmentId}-${warning.stateId}`} className="appointment-warnings__item">
                    <span className="nv-row-8">
                      <i className="pi pi-calendar nv-muted" aria-hidden="true" />
                      {formatWarningDate(warning.appointmentDateTime)}
                    </span>
                    <Tag className={status.tagClass} value={status.label || warning.stateLabel} />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* ── Pie fijo ── */}
      <footer className="nv-page-foot new-appointment-screen__foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          <p className="nv-note nv-push-left">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>Se valida el solapamiento al confirmar.</span>
          </p>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onClose} />
          <span
            className={classNames('new-appointment-screen__confirm nv-tip', { 'nv-disabled-tip': confirmBlockedReason })}
            data-pr-tooltip={confirmBlockedReason ?? undefined}
            tabIndex={confirmBlockedReason ? 0 : undefined}
          >
            <Button
              type="button"
              className="buttonMain"
              label={busy ? 'Confirmando…' : 'Confirmar turno'}
              loading={busy}
              disabled={!canConfirm}
              onClick={() => void handleConfirm()}
            />
          </span>
        </div>
      </footer>
    </div>

    {/* Turno fuera de cupo: decisión antes de tomar el horario */}
    <Dialog
      visible={pendingOverQuotaSlot !== null}
      onHide={() => setPendingOverQuotaSlot(null)}
      header="¿Asignar el turno fuera de cupo?"
      modal
      closable
      draggable={false}
      className="nv-scope nv-dialog nv-dialog-s"
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={() => setPendingOverQuotaSlot(null)} />
          <Button type="button" className="buttonMain" label="Asignar de todas formas" onClick={confirmOverQuotaSlot} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        <span>Este turno supera el cupo asignado para la obra social seleccionada.</span>
      </div>
    </Dialog>
    </>
  );
}
